<template>
  <div ref="domRef" class="h-full">
    <div v-if="!isShowAuthenticate">
      <div v-if="(!isAgent)||(isAgent&&!agentType)" class="three-column-layout">
        <div class="info-card-title">
          企业认证信息
        </div>
        <el-row class="three-column-info-list">
          <el-col :span="8" class="item">
            <div class="label">
              企业编码
            </div>
            <div class="value">
              {{ data.companyCode }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              营业执照
            </div>
            <div class="value">
              <el-link v-if="data.businessLicenseUrl" type="primary" :underline="false" @click="toViewCertificate">
                点击查看
              </el-link>
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              实名审核通过时间
            </div>
            <div class="value">
              {{ data.authTime || '-' }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              认证状态
            </div>
            <div class="value">
              {{ signAuth.authStatus == 3 ? '已认证' : '未认证' }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              数字证书
            </div>
            <div class="value">
              <el-link
                v-if="certificate.status == 'valid'" type="primary" :underline="false" class="mr-[12px]"
                @click="toViewDCCertificate"
              >
                点击查看
              </el-link>
              <el-link
                v-else-if="certificate.status == 'invalid'" type="primary" :underline="false"
                @click="goToRealNameAuthentication(1)"
              >
                重新申领
              </el-link>
            </div>
          </el-col>
        </el-row>
      </div>
      <div v-else-if="isAgent&&agentType" class="three-column-layout">
        <div class="info-card-title">
          个人认证信息
        </div>
        <el-row class="three-column-info-list">
          <el-col :span="8" class="item">
            <div class="label">
              姓名
            </div>
            <div class="value">
              {{ data.name }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              证件类型
            </div>
            <div class="value">
              {{ data.certType ? certTypeConfigLabel[data.certType] : '-' }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              证件号码
            </div>
            <div class="value">
              {{ data.certNo }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              认证方式
            </div>
            <div class="value">
              {{ data.personalAuthType ? personalAuthTypeConfigLabel[data.personalAuthType] : '-' }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              手机号/邮箱
            </div>
            <div class="value">
              {{ data.phone }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              认证状态
            </div>
            <div class="value">
              {{ signAuth.authStatus == 3 ? '已认证' : '未认证' }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              实名审核通过时间
            </div>
            <div class="value">
              {{ data.authTime }}
            </div>
          </el-col>
          <el-col :span="8" class="item">
            <div class="label">
              数字证书
            </div>
            <div class="value">
              <el-link
                v-if="certificate.status == 'valid'" type="primary" :underline="false" class="mr-[12px]"
                @click="toViewDCCertificate"
              >
                点击查看
              </el-link>
              <el-link
                v-else-if="certificate.status == 'invalid'" type="primary" :underline="false"
                @click="goToRealNameAuthentication(1)"
              >
                重新申领
              </el-link>
            </div>
          </el-col>
        </el-row>
      </div>
      <div class="three-column-layout pb-4">
        <div class="info-card-title">
          电子签（授权）
        </div>
        <div class="seal-box">
          <img :src="data.sealUrl" alt="">
        </div>
      </div>
      <el-dialog v-model="dialogVisible" width="800">
        <img class="w-[100%] h-[100%]" :src="dialogImageUrl" alt="Preview Image">
      </el-dialog>
    </div>
    <authenticates v-if="isShowAuthenticate" :form="String(route.name)" :reapply="isReapply" />
  </div>
</template>

<script setup lang="ts">
import { ESystemType, IfreeSignDetailVO, ISignAuthDCInfo, ISignAuthDetail, ISignAuthInfo, querySignAuthDCInfo, querySignAuthDetail } from '@core/api';
import { computed, ref } from 'vue';
import { useSessionStorage } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { ElMessage } from 'element-plus';
import { personalAuthTypeConfigLabel, certTypeConfigLabel } from '@core/main/types';
import { useUserStore, Authenticates } from '@core/main';
import { useElementImgPreview } from '@vue3/components';
defineOptions({ name: 'AuthenticationDetail' });

const route = useRoute();
const isShowAuthenticate = ref(false);
const isReapply = ref(0);
const domRef = ref<HTMLDivElement>();

const signAuth = useSessionStorage<ISignAuthInfo>(
  'signAuth',
  {} as ISignAuthInfo,
);

const dialogImageUrl = ref('');
const dialogVisible = ref(false);
const certificate = ref<ISignAuthDCInfo>({} as ISignAuthDCInfo);

const { state: userState } = useUserStore();
const identity = computed(() => userState.identity);
const agentType = computed(() => identity.value.agentDTO?.agentType);
const isAgent = computed(() => userState.identity.systemType === ESystemType.Agent);

function goToRealNameAuthentication(type: number) {
  isReapply.value = type;
  isShowAuthenticate.value = true;
}

const data = ref<ISignAuthDetail>({} as ISignAuthDetail);
const tableData = ref<IfreeSignDetailVO[]>([]);

const fetchAuthenticationDetail = async () => {
  try {
    data.value = await querySignAuthDetail();
    tableData.value = data.value.freeSignDetailVO;
    try {
      certificate.value = await querySignAuthDCInfo(data.value.fddFlag);

    } catch (error) {
      if ((error as Error).message) ElMessage.error((error as Error).message);
    }
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
};

const toViewDCCertificate = async () => {
  dialogVisible.value = true;
  dialogImageUrl.value = certificate.value.certImg;
};

const toViewCertificate = async () => {
  dialogVisible.value = true;
  dialogImageUrl.value = data.value.businessLicenseUrl;
};

useElementImgPreview(domRef);
fetchAuthenticationDetail();

</script>

<style lang="scss" scoped>
.three-column-info-list .item .label {
  width: 120px;
}

.preview {
  width: 80px;
  height: 60px;
  background-color: rgba(163, 157, 157, 0.2);
  background-size: auto 60px;
  background-repeat: no-repeat;
  background-position: center;
}

.seal-box {
  @apply w-[222px] h-[166px] rounded-[2px] bg-[#FAFAFA] border-[1px] border-[var(--color-split-line)] flex justify-center items-center;

  img {
    @apply w-[206px] h-[150px] rounded-[2px];
  }
}
</style>